// Shared cache + inflight dedup for yt-dlp peak downloads. Without this,
// FeedWaveform, MixFeedWaveform, and WaveformOverlay can each kick off a
// separate yt-dlp process for the same URL, saturating the system and
// causing downloads to time out.
//
// MEMORY: peaks are downsampled to PEAKS_RESOLUTION points before caching.
// The waveform UI is 60px tall × ~screen-wide — even at 4K that's < 4000
// bars. Caching the raw 8kHz Web-Audio decode (~11MB for a 6-min track)
// was wasting ~1500× the memory it needed. The downsampled 2k-point form
// renders an identical-looking waveform and weighs ~8KB per entry, so the
// 100-entry cap is ~1MB total — vs. ~1.1GB worst case unbounded.
import { isSamplesOnly } from './webOnly';

const PEAKS_RESOLUTION = 2000;
const CACHE_MAX = 100;
const cache = new Map();        // url -> { peaks: Float32Array, duration: number }  (insertion-ordered LRU)
const inflight = new Map();     // url -> Promise<{ buffer?, error? }>

// Dev-only introspection (CDP-driven QA reads this to verify cache hits).
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    window.__peaksCacheKeys = () => [...cache.keys()];
}

export function downsamplePeaks(samples, targetLen = PEAKS_RESOLUTION) {
    if (!samples || samples.length <= targetLen) return samples;
    const ratio = samples.length / targetLen;
    const out = new Float32Array(targetLen);
    // RMS aggregation per bucket — preserves waveform energy at display
    // resolution. Peak / abs-max would also work but RMS handles noisy
    // sources better. Either way the eye can't tell at 60px tall.
    for (let i = 0; i < targetLen; i++) {
        const start = Math.floor(i * ratio);
        const end = Math.min(samples.length, Math.floor((i + 1) * ratio));
        let sum = 0;
        const n = end - start;
        for (let j = start; j < end; j++) sum += samples[j] * samples[j];
        out[i] = n > 0 ? Math.sqrt(sum / n) : 0;
    }
    return out;
}

export function getCachedDecoded(url) {
    if (!cache.has(url)) return null;
    // Touch — move to most-recently-used position.
    const entry = cache.get(url);
    cache.delete(url);
    cache.set(url, entry);
    return entry;
}

export function setCachedDecoded(url, entry) {
    if (cache.has(url)) cache.delete(url);
    // Defensive: if caller passes a raw decoded buffer, shrink it before
    // storing. fetchDecodedPeaks below already downsamples, but external
    // setCachedDecoded callers (FeedWaveform's fallback decode path)
    // shouldn't have to know about the size limit.
    const stored = entry?.peaks && entry.peaks.length > PEAKS_RESOLUTION * 2
        ? { ...entry, peaks: downsamplePeaks(entry.peaks) }
        : entry;
    cache.set(url, stored);
    while (cache.size > CACHE_MAX) {
        const oldest = cache.keys().next().value;
        cache.delete(oldest);
    }
    // Notify any FeedWaveform sitting in the withLimit queue waiting for
    // this URL. The bottom player's WaveformOverlay isn't gated by the
    // 3-concurrent feed limiter, so it can populate the cache mid-flight
    // for a freshly-clicked track while feed-row downloads saturate the
    // limiter. Without this event the For You row for that track waits
    // for a slot to open (3-8s) before checking the cache.
    try {
        window.dispatchEvent(new CustomEvent('wf-peaks-cached', { detail: { url } }));
    } catch { /* SSR / no window */ }
}

// Returns the raw { buffer? , error? } shape from the main-process IPC.
// Dedupes concurrent callers to a single yt-dlp invocation.
export async function fetchPeaksRaw(url, opts) {
    // Samples-only platforms (iOS, Android, web, mobile web) have no
    // yt-dlp bridge — `downloadForPeaks` resolves to null via the
    // capacitor-shim no-op, but we still spend an IPC round-trip per
    // visible feed row. Short-circuit so callers fall straight to the
    // sample-URL AudioContext decode path (the only one that works
    // outside Electron desktop anyway).
    if (isSamplesOnly) return { error: 'unavailable' };
    if (!url || !window.electron?.djpal?.downloadForPeaks) {
        return { error: 'unavailable' };
    }
    if (inflight.has(url)) return inflight.get(url);
    const p = window.electron.djpal.downloadForPeaks(url, opts).finally(() => {
        inflight.delete(url);
    });
    inflight.set(url, p);
    return p;
}

// Dedup map so concurrent callers for one URL decode the ArrayBuffer only once.
const decodeInflight = new Map();

// Chunked RMS downsample for LARGE decodes (an hour-long mix at 8kHz is
// ~36M samples): one synchronous pass blocked the main thread for hundreds
// of ms — visible as a frozen play-button spinner when a big decode landed
// mid-interaction. Same math as downsamplePeaks, yielding between slices.
async function downsamplePeaksChunked(samples, targetLen = PEAKS_RESOLUTION) {
    if (!samples || samples.length <= targetLen) return samples;
    const ratio = samples.length / targetLen;
    const out = new Float32Array(targetLen);
    const SLICE = 250; // buckets per slice ≈ a few ms of work each
    for (let i = 0; i < targetLen; i++) {
        if (i > 0 && i % SLICE === 0) await new Promise((r) => setTimeout(r, 0));
        const start = Math.floor(i * ratio);
        const end = Math.min(samples.length, Math.floor((i + 1) * ratio));
        let sum = 0;
        const n = end - start;
        for (let j = start; j < end; j++) sum += samples[j] * samples[j];
        out[i] = n > 0 ? Math.sqrt(sum / n) : 0;
    }
    return out;
}

// Decode an ArrayBuffer → cached { peaks, duration } under `cacheKey`.
//
// OfflineAudioContext, NOT AudioContext: Chromium caps live hardware
// AudioContexts at ~6 per page, and a context created past the cap doesn't
// throw — its decodeAudioData just NEVER SETTLES. With one context per
// decode, several concurrent mix decodes + feed-row sample decodes crossed
// the cap, the hung decode leaked its context (close never ran), and every
// later decode was poisoned — the promise sat in decodeInflight forever, so
// the waveform for that URL could never load again without an app restart.
// OfflineAudioContext is not a hardware context and has no such cap. The
// decode timeout is belt-and-braces: if a decode ever wedges again, the
// caller gets null (→ failed state / fake waveform) instead of a
// stuck-forever loader, and the map entry frees for a retry.
const DECODE_TIMEOUT_MS = 90000;
async function _decodeAndCache(buffer, cacheKey, tag) {
    try {
        const audioCtx = new OfflineAudioContext(1, 8000, 8000);
        const decoded = await Promise.race([
            audioCtx.decodeAudioData(buffer),
            new Promise((_, reject) => setTimeout(() => reject(new Error('decode timeout')), DECODE_TIMEOUT_MS)),
        ]);
        // Yield, then downsample in slices — hour-long decodes are tens of
        // millions of samples and must not block the main thread in one go.
        await new Promise((r) => setTimeout(r, 0));
        const peaks = await downsamplePeaksChunked(decoded.getChannelData(0));
        const entry = { peaks, duration: decoded.duration };
        if (cacheKey) setCachedDecoded(cacheKey, entry);
        return entry;
    } catch (e) {
        console.warn(`[wf] peaksCache decode failed ${tag}`, e);
        return null;
    }
}

// High-level: download (via yt-dlp) + decode once, cache decoded peaks.
// Returns { peaks, duration } or null on failure — NEVER rejects: a thrown
// error here used to skip the callers' null-handling (MixFeedWaveform's
// failed state, WaveformOverlay's reveal) and leave loaders up forever.
// Decoded result is deduped across concurrent callers so we decode the
// ArrayBuffer only once.
export async function fetchDecodedPeaks(url, opts) {
    const cached = getCachedDecoded(url);
    if (cached) { return cached; }
    if (decodeInflight.has(url)) { return decodeInflight.get(url); }

    const job = (async () => {
        try {
            const result = await fetchPeaksRaw(url, opts);
            if (!result || !result.buffer || result.error) return null;
            return await _decodeAndCache(result.buffer, url, (url || '').slice(-40));
        } catch (e) {
            console.warn('[wf] fetchDecodedPeaks failed', (url || '').slice(-40), e);
            return null;
        }
    })().finally(() => decodeInflight.delete(url));

    decodeInflight.set(url, job);
    return job;
}
